(ns manage.adset-decision-tree-page.bulk-apply-trackers-ocp.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [useGridApiContext]]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            [cljs-bean.core :refer [->clj]]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.ocp.view :refer [defocp ocp]]
            [common.validators.core :as validators]
            [common.widgets.data-grid :as dg]
            [manage.adset-decision-tree-page.bulk-apply-trackers-ocp.hook :as hook]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.property :as property]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.side-panel.editors :refer [inherited-adornment]]
            [manage.adset-decision-tree-page.view.side-panel.trackers
             :refer [add-new-tracker-button]]
            [manage.adset-decision-tree-page.view.tracker-table :refer [tracker-table]]
            [manage.adset.hooks :as ah]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui ^:memo tracker-edit-cell [{:keys [read-only idx type]
                                  {:keys [id value field hasFocus]} :params}]
  (let [{{:keys [setEditCellValue startCellEditMode]} :current} (->clj (useGridApiContext))
        [local-value set-local-value] (uix/use-state nil)
        [has-on-blur set-has-on-blur] (uix/use-state false)
        handle-change (fn [new-value]
                        (set-local-value new-value)
                        (setEditCellValue #js {:id id
                                               :field field
                                               :value new-value
                                               :debounceMs 200}))
        inherited-node-data (use-subscribe [::sub/root-node-data])
        inherited-value (get-in (filterv
                                 #(= type (:type %))
                                 (get-in inherited-node-data property/trackers-data-path))
                                [(or idx 0) :value])
        input-value (if read-only
                      (if (map? value)
                        (let [unique-count (count value)]
                          (str unique-count " tracker"
                               (when (< 1 unique-count) "s")
                               " configured"))
                        value)
                      local-value)
        error (when (and has-on-blur (seq local-value) (not (validators/url? local-value)))
                "should be a URL")]
    (uix/use-effect (fn [] (when-not (map? value) (set-local-value value))) [value])
    ($ TextField
       {:value (or input-value "")
        :placeholder (or inherited-value "Paste tracker URL...")
        :slotProps #js {:input #js {:endAdornment
                                    (if (some? inherited-value)
                                      ($ inherited-adornment
                                         {:inherited-value inherited-value
                                          :node-data inherited-node-data
                                          :value input-value
                                          :set-value handle-change})
                                      (when (some? input-value)
                                        ($ IconButton {:on-click #(handle-change nil)}
                                           ($ Icon "clear"))))
                                    :readOnly (boolean read-only)}}
        :size "small"
        :error (boolean error)
        :helperText error
        :fullWidth true
        :autoFocus hasFocus
        :sx #js {:px (when-not read-only 2)}
        :variant "outlined"
        :onChange (when-not read-only (fn [^js e] (handle-change (.. e -target -value))))
        :onClick (when read-only
                   (fn [^js e]
                     (.stopPropagation e)
                     (startCellEditMode #js {:id id
                                             :field field})))
        :onBlur #(set-has-on-blur true)})))

(defn- tracker-render-cell [details ^js params]
  ($ tracker-edit-cell (merge details
                              {:params (->clj params)
                               :read-only true})))

(defn- tracker-render-edit-cell [details ^js params]
  ($ tracker-edit-cell (merge details {:params (->clj params)})))

(defui tracker-column-header [{:keys [type on-delete field idx]}]
  ($ Stack {:direction "row"
            :alignItems "center"
            :sx #js {:minWidth 0
                     :width "100%"
                     "& .delete-icon" #js {:display "none"}
                     "&:hover .delete-icon" #js {:display "block"}}}
     ($ Typography {:noWrap true}
        (if (= type "click")
          "Click trackers"
          (str "Impression trackers" (when (pos? idx) (str " (" (inc idx) ")")))))
     ($ IconButton {:size "small"
                    :className "delete-icon"
                    :onClick (fn [^js e]
                               (.stopPropagation e)
                               (on-delete field))
                    :sx #js {:p 0.5}}
        ($ Icon "delete"))))

(defn- tracker-column [{:keys [field] :as details}]
  {:field field
   :flex 2
   :minWidth 200
   :editable true
   :sortable false
   :disableColumnMenu true
   :renderCell (partial tracker-render-cell details)
   :renderEditCell (partial tracker-render-edit-cell details)
   :renderHeader (fn [^js _params] ($ tracker-column-header details))})

(defn- build-extra-columns [{:keys [impression-columns delete-column has-click-column]}]
  (cond-> (vec (map-indexed (fn [idx field]
                              (tracker-column {:idx idx
                                               :field field
                                               :type "impression"
                                               :on-delete delete-column}))
                            impression-columns))
    has-click-column (conj (tracker-column {:field "clickTrackers"
                                            :type "click"
                                            :on-delete delete-column}))))

(defui footer [{:keys [on-save]}]
  ($ Stack
     {:direction "row"
      :spacing 1
      :justifyContent "flex-end"}
     ($ Button
        {:variant "outlined"
         :onClick #(rf/dispatch [:ocp/close])}
        "Cancel")
     ($ Button
        {:variant "contained"
         :onClick on-save}
        "Save")))

(defmodal overwrite-warning ::overwrite-warning
  [{:keys [on-save]}]
  ($ modal-v2
     {:icon ($ SvgIcon {:color "warning"} ($ AlertIcon))
      :title "Overwrite Tracker Values"
      :footer ($ Stack {:direction "row" :spacing 1}
                 ($ Button {:variant "outlined"
                            :on-click #(rf/dispatch [:modal/close])}
                    "Cancel")
                 ($ Button {:variant "contained"
                            :color "warning"
                            :on-click on-save}
                    "Save"))}
     ($ Typography {:sx #js {:maxWidth 600}}
        "You’re about to overwrite certain tracker values. Please confirm to continue.")))

(defocp ocp-view ::ocp {:width "80%"} []
  (let [initial-rows (hook/use-initial-rows)
        {:keys [rows process-row-update remove-column-data]}
        (hook/use-filtered-editable-rows initial-rows)
        {:keys [impression-columns has-click-column set-has-click-column
                add-impression-column] :as column-controls}
        (hook/use-columns-controls {:remove-column-data remove-column-data
                                    :initial-rows initial-rows})
        channel (ah/use-channel)
        extra-columns (uix/use-memo #(build-extra-columns column-controls) [column-controls])
        toolbar-extra (uix/use-memo
                       (fn []
                         ($ add-new-tracker-button
                            {:is-click-tracker-item-enabled (not has-click-column)
                             :is-impression-item-shown (not= channel "meta")
                             :add-new-click-tracker #(set-has-click-column true)
                             :add-new-impression-tracker add-impression-column
                             :is-column true}))
                       [set-has-click-column has-click-column channel add-impression-column])
        on-save (uix/use-callback
                 #(let [{:keys [valid bulk-patch-data has-overwrites]}
                        (hook/get-save-data rows impression-columns initial-rows)
                        on-save
                        (fn [] (rf/dispatch [::event/bulk-apply-trackers bulk-patch-data]))]
                    (when valid
                      (if (seq bulk-patch-data)
                        (if has-overwrites
                          (rf/dispatch [:modal/open {:id ::overwrite-warning
                                                     :props {:on-save on-save}}])
                          (on-save))
                        (rf/dispatch [:ocp/close]))))
                 [rows initial-rows impression-columns])]
    ($ ocp {:title "Bulk apply trackers"
            :footer ($ footer {:on-save on-save})}
       ($ Stack {:spacing 2
                 :sx #js {:height "100%"}}
          ($ tracker-table
             {:rows rows
              :extra-columns extra-columns
              :process-row-update process-row-update
              :toolbar-extra toolbar-extra
              :sx #js {"& .MuiDataGrid-columnHeaderTitleContainerContent"
                       #js {:width "100%"}}})))))
